import React, { useState } from 'react';
import { CloseIcon, EventIcon, ImageIcon, PollIcon, ViewListIcon } from '@/components/ui/Icons';
import { ImageLightbox } from '@/components/ui/ImageLightbox/ImageLightbox';
import { getAttachmentBadgeCategory, getFileExtensionLabel } from './attachmentBadgeCategory';
import { clampAttachmentCardTitle } from './clampAttachmentCardTitle';
import './AttachmentCard.css';

export interface AttachmentCardProps {
  variant: 'file' | 'image' | 'project' | 'session' | 'planner';
  /** Контент над строкой `header` внутри текстовой колонки. */
  textColPrefix?: React.ReactNode;
  /** Шапка карточки над рядом бейджа и текста (имя в группе, «Переслано от…» в DM). */
  topHeader?: React.ReactNode;
  header?: string | null;
  title: string;
  subtitle?: string;
  src?: string;
  href?: string;
  onClick?: () => void;
  onRemove?: () => void;
  /** Напр. `chatAssistant__close` — тот же стиль, что у крестика в шапке чата */
  removeButtonClassName?: string;
  busy?: boolean;
  busyLabel?: string;
  className?: string;
}

export function AttachmentCard({
  variant,
  textColPrefix,
  topHeader,
  header,
  title,
  subtitle,
  src,
  href,
  onClick,
  onRemove,
  removeButtonClassName,
  busy,
  busyLabel,
  className = '',
}: AttachmentCardProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const isDownloadLink = Boolean(href && !onClick && variant !== 'image');
  const imageOpensLightbox = variant === 'image' && Boolean(src);
  const isClickable = Boolean(onClick || isDownloadLink || imageOpensLightbox);

  const handleInteraction = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (e.type === 'keydown' && (e as React.KeyboardEvent).key !== 'Enter' && (e as React.KeyboardEvent).key !== ' ') {
      return;
    }
    if (e.type === 'keydown' && (e as React.KeyboardEvent).key === ' ') {
      e.preventDefault();
    }

    if (imageOpensLightbox) {
      e.preventDefault();
      setLightboxOpen(true);
      return;
    }

    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  const extLabel = variant === 'file' ? getFileExtensionLabel(title) || 'FILE' : '';

  const fileBadgeCat = variant === 'file' ? getAttachmentBadgeCategory(title) : '';
  const titleShown = clampAttachmentCardTitle(title);

  const removeBtnClass = ['attachmentCard__removeBtn', removeButtonClassName].filter(Boolean).join(' ');
  const removeIconSize = removeButtonClassName ? 20 : 16;

  const renderBadge = () => {
    if (variant === 'image') {
      if (src) {
        return <img src={src} alt="" className="attachmentCard__thumb" draggable={false} />;
      }
      return (
        <span className="attachmentCard__badge attachmentCard__badge_kind_image" data-badge-cat="image" aria-hidden>
          <ImageIcon size={22} className="attachmentCard__badgeIcon" />
        </span>
      );
    }

    if (variant === 'file') {
      return (
        <span
          className="attachmentCard__badge attachmentCard__badge_kind_file"
          data-badge-cat={fileBadgeCat}
          aria-hidden
        >
          <span className="attachmentCard__badgeExt">{extLabel}</span>
        </span>
      );
    }

    if (variant === 'project') {
      return (
        <span className="attachmentCard__badge attachmentCard__badge_kind_project" aria-hidden>
          <ViewListIcon size={22} className="attachmentCard__badgeIcon" />
        </span>
      );
    }

    if (variant === 'session') {
      return (
        <span className="attachmentCard__badge attachmentCard__badge_kind_session" aria-hidden>
          <EventIcon size={22} className="attachmentCard__badgeIcon" />
        </span>
      );
    }

    if (variant === 'planner') {
      return (
        <span className="attachmentCard__badge attachmentCard__badge_kind_planner" aria-hidden>
          <PollIcon size={22} className="attachmentCard__badgeIcon" />
        </span>
      );
    }

    return null;
  };

  const mainRow = (
    <>
      <div className="attachmentCard__badgeCol">{renderBadge()}</div>
      <div className="attachmentCard__textCol">
        {textColPrefix}
        {header ? (
          <span className="attachmentCard__header" title={header}>
            {header}
          </span>
        ) : null}
        <span className="attachmentCard__title" title={title}>
          {titleShown}
        </span>
        {subtitle ? (
          <span className="attachmentCard__subtitle" title={subtitle}>
            {subtitle}
          </span>
        ) : null}
      </div>
      {onRemove ? (
        <button
          type="button"
          className={removeBtnClass}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          aria-label="Удалить"
          title="Удалить"
        >
          <CloseIcon size={removeIconSize} />
        </button>
      ) : null}
    </>
  );

  const busyOverlay = busy ? (
    <div className="attachmentCard__busyOverlay" aria-busy="true" aria-label={busyLabel}>
      <span className="attachmentCard__busySpinner" />
    </div>
  ) : null;

  const downloadAria = isDownloadLink ? `Скачать файл: ${title}` : undefined;

  const shellClasses = [
    'attachmentCard',
    `attachmentCard_variant_${variant}`,
    topHeader ? 'attachmentCard_hasTopHeader' : '',
    !topHeader && isClickable ? 'attachmentCard_clickable' : '',
    !topHeader && isDownloadLink ? 'attachmentCard_download' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const mainLinkClasses = [
    'attachmentCard__mainLink',
    topHeader && isClickable ? 'attachmentCard_clickable' : '',
    topHeader && isDownloadLink ? 'attachmentCard_download' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const renderMainLink = () => {
    const mainBody = <div className="attachmentCard__mainRow">{mainRow}</div>;

    if (isDownloadLink) {
      return (
        <a
          href={href}
          download={title}
          target="_blank"
          rel="noopener noreferrer"
          className={mainLinkClasses}
          aria-label={downloadAria}
        >
          {mainBody}
        </a>
      );
    }

    return (
      <div
        className={mainLinkClasses}
        onClick={isClickable ? handleInteraction : undefined}
        onKeyDown={isClickable ? handleInteraction : undefined}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        aria-label={imageOpensLightbox ? `Просмотр: ${title}` : undefined}
      >
        {mainBody}
      </div>
    );
  };

  if (topHeader) {
    return (
      <>
        <div className={shellClasses} aria-busy={busy || undefined}>
          <div className="attachmentCard__topHeader">{topHeader}</div>
          {renderMainLink()}
          {busyOverlay}
        </div>
        {imageOpensLightbox ? (
          <ImageLightbox src={src!} alt={title} open={lightboxOpen} onClose={() => setLightboxOpen(false)} />
        ) : null}
      </>
    );
  }

  const cardContent = (
    <>
      <div className="attachmentCard__mainRow">{mainRow}</div>
      {busyOverlay}
    </>
  );

  return (
    <>
      {isDownloadLink ? (
        <a
          href={href}
          download={title}
          target="_blank"
          rel="noopener noreferrer"
          className={shellClasses}
          aria-label={downloadAria}
          aria-busy={busy || undefined}
        >
          {cardContent}
        </a>
      ) : (
        <div
          className={shellClasses}
          onClick={isClickable ? handleInteraction : undefined}
          onKeyDown={isClickable ? handleInteraction : undefined}
          role={isClickable ? 'button' : undefined}
          tabIndex={isClickable ? 0 : undefined}
          aria-label={imageOpensLightbox ? `Просмотр: ${title}` : undefined}
          aria-busy={busy || undefined}
        >
          {cardContent}
        </div>
      )}

      {imageOpensLightbox ? (
        <ImageLightbox src={src!} alt={title} open={lightboxOpen} onClose={() => setLightboxOpen(false)} />
      ) : null}
    </>
  );
}
